import React, { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Button, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';
import { tableColumns } from './OnboardingVideosData';
import CreateOnboardingVideo from './CreateOnboardingVideo';
import EditOnboardingVideo from './EditOnboardingVideo';
import EditIcon from '../../assets/images/svg/editLearning.svg';
import Search from '../../assets/images/svg/Search_SoS.svg';
import SearchIcon from '../../assets/images/svg/search_iconSos.svg';
import Filter from '../../assets/images/svg/Filter_SoS.svg';

// ─── Table styles ─────────────────────────────────────────────────────────────
const tableStyles = `
  .rdt_TableCell { padding: 4px 2px !important; }
  .rdt_TableHead { padding: 4px 2px !important; }
  .rdt_TableCol  { padding: 4px 2px !important; }

  .rdt_TableCell[data-column-id="action"] {
    position: sticky !important; right: 0 !important;
    background-color: inherit !important; z-index: 1;
  }
  .rdt_TableHeadRow [data-column-id="action"] {
    position: sticky !important; right: 0 !important;
    background-color: inherit !important; z-index: 1;
  }
  .data-tables { overflow-x: auto; }
  .btn-link:hover { text-decoration: none !important; }
  .btn[disabled] { cursor: not-allowed; }
  .btn[title]:hover:after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 8px;
    color: white;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 10;
  }
  .btn[title] { position: relative; }
`;

// ─── Component ────────────────────────────────────────────────────────────────
const OnboardingVideosTable = () => {
    const [data, setData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);

    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editRowData, setEditRowData] = useState(null);

    // Confirm modal (delete / toggle status)
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({ type: '', id: null, currentStatus: null });

    // Search / filter
    const [searchValue, setSearchValue] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [showDropdown, setShowDropdown] = useState(false);

    const searchRef = useRef(null);
    const dropdownRef = useRef(null);

    const filterOptions = [
        { label: 'All', value: 'all' },
        { label: 'Active', value: '1' },
        { label: 'Inactive', value: '0' },
    ];

    // ── click outside handlers ──────────────────────────────────────
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false);
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── prepare rows (inject action buttons) ───────────────────────
    const prepareData = (items) =>
        items.map(item => ({
            ...item,
            action: (
                <div className="d-flex justify-content-center gap-2">
                    {/* Edit */}
                    <button
                        className="btn btn-link p-0"
                        title="Edit"
                        onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                    >
                        <img src={EditIcon} alt="edit" style={{ width: '24px', height: '24px' }} />
                    </button>

                    {/* Toggle active / inactive */}
                    <div title={item.status === 1 ? 'Deactivate' : 'Activate'}>
                        <button
                            className="btn btn-link p-0"
                            disabled={false}
                            onClick={(e) => {
                                e.stopPropagation();
                                setModalConfig({ type: 'toggle', id: item.id, currentStatus: item.status });
                                setShowModal(true);
                            }}
                            style={{ opacity: 1 }}
                        >
                            {item.status === 1 ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00C853" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="9 12 11 14 15 10" />
                                </svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9E9E9E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="15" y1="9" x2="9" y2="15" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {/* Delete */}
                    <div title="Delete">
                        <button
                            className="btn btn-link p-0"
                            onClick={(e) => {
                                e.stopPropagation();
                                setModalConfig({ type: 'delete', id: item.id, currentStatus: item.status });
                                setShowModal(true);
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <path d="M10 11v6M14 11v6" />
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                        </button>
                    </div>
                </div>
            ),
        }));

    // ── fetch ───────────────────────────────────────────────────────
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await _axios.get(`${ServerPath}/onboarding-videos/admin`);
            if (response.data.success) {
                const prepared = prepareData(response.data.data);
                setData(prepared);
                setOriginalData(prepared);
                setTotalRows(response.data.total ?? prepared.length);
            } else {
                toast.error('Failed to fetch onboarding videos');
            }
        } catch (error) {
            console.error('[OnboardingVideosTable] fetchData error:', error);
            toast.error('Failed to fetch onboarding videos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // ── search / filter ─────────────────────────────────────────────
    const applyFilters = (search, filter) => {
        let filtered = [...originalData];

        if (search?.trim()) {
            const term = search.toLowerCase();
            filtered = filtered.filter(item =>
                String(item.id || '').includes(term) ||
                String(item.title || '').toLowerCase().includes(term) ||
                String(item.title_hin || '').toLowerCase().includes(term) ||
                String(item.app_version || '').toLowerCase().includes(term)
            );
        }

        if (filter !== 'all') {
            filtered = filtered.filter(item => String(item.status) === filter);
        }

        setData(filtered);
    };

    const handleSearch = (value) => {
        setSearchValue(value);
        applyFilters(value, selectedFilter);
    };

    const handleFilterChange = (value) => {
        setSelectedFilter(value);
        applyFilters(searchValue, value);
        setShowDropdown(false);
    };

    // ── crud handlers ───────────────────────────────────────────────
    const handleEdit = (row) => {
        setEditRowData(row);
        setShowEdit(true);
    };

    const handleModalConfirm = async () => {
        try {
            setLoading(true);

            if (modalConfig.type === 'delete') {
                const response = await _axios.delete(
                    `${ServerPath}/onboarding-videos/admin/${modalConfig.id}`
                );
                if (response.data.success) {
                    toast.success('Video deleted successfully');
                    await fetchData();
                } else {
                    toast.error(response.data.message || 'Failed to delete video');
                }
            }

            if (modalConfig.type === 'toggle') {
                const newStatus = modalConfig.currentStatus === 1 ? 0 : 1;
                const response = await _axios.patch(
                    `${ServerPath}/onboarding-videos/admin/${modalConfig.id}/status`,
                    { status: newStatus }
                );
                if (response.data.success) {
                    toast.success(response.data.message);
                    await fetchData();
                } else {
                    toast.error(response.data.message || 'Failed to update status');
                }
            }
        } catch (error) {
            console.error('[OnboardingVideosTable] modalConfirm error:', error);
            toast.error(error.response?.data?.message || 'Operation failed');
        } finally {
            setLoading(false);
            setShowModal(false);
        }
    };

    const handleEditClose = () => {
        setShowEdit(false);
        setEditRowData(null);
        fetchData();
    };

    const handleCreateClose = () => {
        setShowCreate(false);
        fetchData();
    };

    // ── modal text helpers ──────────────────────────────────────────
    const modalTitle = () => {
        if (modalConfig.type === 'delete') return 'Delete Video';
        if (modalConfig.type === 'toggle')
            return modalConfig.currentStatus === 1 ? 'Deactivate Video' : 'Activate Video';
        return '';
    };

    const modalBody = () => {
        if (modalConfig.type === 'delete')
            return 'Are you sure you want to permanently delete this video? This action cannot be undone.';
        if (modalConfig.type === 'toggle')
            return modalConfig.currentStatus === 1
                ? 'Are you sure you want to deactivate this video? It will no longer appear in the app.'
                : 'Are you sure you want to activate this video?';
        return '';
    };

    const modalBtnLabel = () => {
        if (modalConfig.type === 'delete') return 'Delete';
        return modalConfig.currentStatus === 1 ? 'Deactivate' : 'Activate';
    };

    const modalBtnVariant = () =>
        modalConfig.type === 'delete' ? 'danger' : 'primary';

    // ── render ──────────────────────────────────────────────────────
    return (
        <Fragment>
            <style>{tableStyles}</style>
            <Container fluid={true}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mb-0">Onboarding Videos</h3>

                    <div className="d-flex align-items-center gap-3">
                        {/* Search */}
                        <div className="position-relative" ref={searchRef}>
                            <button
                                className="bg-transparent border-0"
                                onClick={() => setShowSearch(true)}
                            >
                                <img src={Search} alt="search" style={{ width: '36px', height: '36px' }} />
                            </button>

                            {showSearch && (
                                <div style={{
                                    position: 'absolute',
                                    top: '45px',
                                    left: '-180px',
                                    width: '220px',
                                    zIndex: 1000,
                                }}>
                                    <div style={{
                                        background: 'white',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        padding: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}>
                                        <div style={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: '#F8F7FA',
                                            borderRadius: '6px',
                                            padding: '6px 12px',
                                        }}>
                                            <input
                                                type="text"
                                                placeholder="Search Here..."
                                                value={searchValue}
                                                onChange={(e) => handleSearch(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    border: 'none',
                                                    background: 'transparent',
                                                    fontSize: '14px',
                                                    color: '#666',
                                                    outline: 'none',
                                                }}
                                                autoFocus
                                            />
                                            <button
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                                onClick={() => handleSearch(searchValue)}
                                            >
                                                <img src={SearchIcon} alt="search" style={{ width: '20px', height: '20px' }} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Filter dropdown */}
                        <div className="position-relative" ref={dropdownRef}>
                            <button
                                className="bg-transparent border-0"
                                onClick={() => setShowDropdown(!showDropdown)}
                            >
                                <img src={Filter} alt="filter" style={{ width: '36px', height: '36px' }} />
                            </button>

                            {showDropdown && (
                                <div className="position-absolute end-0 mt-2 bg-white rounded shadow p-2"
                                    style={{ width: '200px', zIndex: 1000 }}>
                                    <Form>
                                        {filterOptions.map((option) => (
                                            <Form.Check
                                                key={option.value}
                                                type="checkbox"
                                                name="filterOption"
                                                id={`filter-${option.value}`}
                                                label={option.label}
                                                checked={selectedFilter === option.value}
                                                onChange={() => handleFilterChange(option.value)}
                                            />
                                        ))}
                                    </Form>
                                </div>
                            )}
                        </div>

                        {/* Add button */}
                        <Button
                            variant="primary"
                            onClick={() => setShowCreate(true)}
                            className="bg-primary"
                        >
                            + Add Video
                        </Button>
                    </div>
                </div>

                {/* DataTable */}
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody>
                                <div className="data-tables">
                                    <DataTable
                                        columns={tableColumns}
                                        data={data}
                                        progressPending={loading}
                                        pagination
                                        paginationPerPage={10}
                                        paginationRowsPerPageOptions={[10, 25, 50]}
                                        highlightOnHover
                                        responsive
                                        noDataComponent={
                                            <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
                                                No onboarding videos found
                                            </div>
                                        }
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Create modal */}
            <CreateOnboardingVideo
                show={showCreate}
                onHide={handleCreateClose}
            />

            {/* Edit modal */}
            <EditOnboardingVideo
                show={showEdit}
                onHide={handleEditClose}
                videoData={editRowData}
            />

            {/* Confirm modal (delete / toggle) */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Body className="text-center p-4">
                    <div className="mb-4">
                        <div className="rounded-circle bg-warning bg-opacity-10 d-inline-flex p-4 mb-3">
                            <i className="bi bi-exclamation-lg fs-4 text-warning"></i>
                        </div>
                        <h4>{modalTitle()}</h4>
                        <p className="text-secondary mb-0">{modalBody()}</p>
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button
                            variant={modalConfig.type === 'delete' ? 'danger' : 'primary'}
                            className="px-4"
                            onClick={handleModalConfirm}
                            disabled={loading}
                        >
                            {loading ? 'Processing…' : modalBtnLabel()}
                        </Button>
                        <Button variant="danger" className="px-4" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </Fragment>
    );
};

export default OnboardingVideosTable;
